import { isClosedJobStatus, type JobResponse, jobResponseSchema } from '@parkchirp/reporting';
import ky from 'ky';
import { useEffect, useMemo, useState } from 'react';

type UseReportJobPollingArgs = {
	authToken: string;
	baseUrl: string;
	jobId?: string;
	pollIntervalMs?: number;
};

type UseReportJobPollingResult = {
	job: JobResponse | null;
	error: string | null;
	isPolling: boolean;
};

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : 'Failed to get report status.');

export const useReportJobPolling = ({
	authToken,
	baseUrl,
	jobId,
	pollIntervalMs = 2_000,
}: UseReportJobPollingArgs): UseReportJobPollingResult => {
	const [job, setJob] = useState<JobResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isPolling, setIsPolling] = useState(false);

	const api = useMemo(
		() =>
			ky.create({
				prefixUrl: baseUrl,
				timeout: 30_000,
				retry: { limit: 0 },
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
			}),
		[authToken, baseUrl],
	);

	useEffect(() => {
		setJob(null);
		setError(null);

		if (!authToken || !baseUrl || !jobId) {
			setIsPolling(false);
			return;
		}

		let active = true;
		let timeout: ReturnType<typeof setTimeout> | undefined;
		const abortController = new AbortController();

		setIsPolling(true);

		const poll = async () => {
			try {
				const response = await api.get(`jobs/${jobId}`, { signal: abortController.signal }).json<unknown>();
				const nextJob = jobResponseSchema.parse(response);

				if (!active || nextJob.jobId !== jobId) {
					return;
				}

				setJob(nextJob);

				if (isClosedJobStatus(nextJob.status)) {
					setIsPolling(false);
					return;
				}

				timeout = setTimeout(poll, pollIntervalMs);
			} catch (pollError) {
				if (!active || abortController.signal.aborted) {
					return;
				}

				setError(getErrorMessage(pollError));
				setIsPolling(false);
			}
		};

		poll();

		return () => {
			active = false;
			abortController.abort();
			if (timeout) clearTimeout(timeout);
		};
	}, [api, authToken, baseUrl, jobId, pollIntervalMs]);

	return { job, error, isPolling };
};
